/**
 * Split Hero — One-shot video intro hero
 *
 * Architecture:
 *   On page load, a full-screen fixed overlay shows a static image with two
 *   invisible click zones (left/right). The user clicks a zone or scrolls to
 *   trigger the video. Once triggered, the video plays start-to-finish at a
 *   configurable playback rate. At contentRevealPoint% of video duration, a
 *   crossfade begins: the hero fades out while the header + page content fades
 *   in simultaneously. The video cannot be replayed without refreshing the page
 *   (clearing the ?side= URL parameter).
 *
 *   Clicking left/right updates the URL to ?side=left or ?side=right via
 *   history.replaceState. On subsequent visits with ?side= present, the
 *   split-hero section is not rendered at all (handled server-side in
 *   theme.liquid), so this JS never loads.
 *
 *   The chosen side also controls which homepage section group is visible
 *   (#left-show-group or #right-show-group).
 */

class SplitHero {
  constructor(sectionEl) {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // DOM refs
    this.section = sectionEl;
    this.overlayEl = sectionEl.querySelector(".split-hero__overlay");
    this.staticMedia = sectionEl.querySelector(".split-hero__media--static");
    this.leftZone = sectionEl.querySelector(".split-hero__zone--left");
    this.rightZone = sectionEl.querySelector(".split-hero__zone--right");
    this.leftVideoWrapper = sectionEl.querySelector(".split-hero__video-wrapper--left");
    this.rightVideoWrapper = sectionEl.querySelector(".split-hero__video-wrapper--right");

    // Settings from data attributes
    this.defaultSide = sectionEl.dataset.defaultSide || "right";
    this.playbackSpeed = parseFloat(sectionEl.dataset.playbackSpeed) || 1.0;
    this.contentRevealPoint = parseFloat(sectionEl.dataset.contentRevealPoint);
    if (isNaN(this.contentRevealPoint)) this.contentRevealPoint = 0.5;
    this.splitPercentage = parseFloat(sectionEl.dataset.splitPercentage) || 50;
    this.scrollThreshold = parseFloat(sectionEl.dataset.scrollThreshold) || 15;

    // State
    this.hasPlayed = false;
    this.activeSide = null;

    // Set zone widths
    if (this.leftZone) {
      this.leftZone.style.width = this.splitPercentage + "%";
    }
    if (this.rightZone) {
      this.rightZone.style.width = 100 - this.splitPercentage + "%";
    }
  }

  /**
   * Initialise the hero: lock body scroll, set up click + scroll listeners.
   */
  init() {
    if (this.reducedMotion) {
      this.skipHero();
      return;
    }

    // If URL already has ?side=, skip the video immediately (returning visitor)
    // and show the correct section group.
    if (window.location.search.indexOf("side=") !== -1) {
      this.skipHero();
      var sideParam = window.location.search.match(/side=([^&]*)/);
      var side = sideParam && sideParam[1] ? sideParam[1] : this.defaultSide;
      this.showSectionGroup(side);
      return;
    }

    // Lock body and hide content behind overlay
    document.body.classList.add("split-hero-active");

    // Click listeners on left/right zones
    if (this.leftZone) {
      this.leftZone.addEventListener("click", () => this.activate("left"));
    }
    if (this.rightZone) {
      this.rightZone.addEventListener("click", () => this.activate("right"));
    }

    // First scroll triggers the default side (desktop: wheel)
    this._onFirstScroll = () => this.activate(this.defaultSide);
    window.addEventListener("wheel", this._onFirstScroll, {once: true, passive: true});

    // Touch gesture tracking — distinguish taps from scrolls on mobile.
    // A tap (distance ≤ scrollThreshold) activates the side the user touched.
    // A swipe (distance > scrollThreshold) activates the default side.
    this._boundTouchStart = this._onTouchStart.bind(this);
    this._boundTouchEnd = this._onTouchEnd.bind(this);
    window.addEventListener("touchstart", this._boundTouchStart, {passive: true});
    window.addEventListener("touchend", this._boundTouchEnd, {passive: true});
  }

  /**
   * Record the starting touch position.
   * @param {TouchEvent} e
   */
  _onTouchStart(e) {
    if (this.hasPlayed) return;
    this._touchStartX = e.touches[0].clientX;
    this._touchStartY = e.touches[0].clientY;
  }

  /**
   * Determine whether the touch was a tap or a scroll.
   * Tap → side based on X position.  Scroll → default side.
   * @param {TouchEvent} e
   */
  _onTouchEnd(e) {
    if (this.hasPlayed) return;
    if (this._touchStartX == null) return;

    var dx = e.changedTouches[0].clientX - this._touchStartX;
    var dy = e.changedTouches[0].clientY - this._touchStartY;
    var distance = Math.sqrt(dx * dx + dy * dy);

    this._touchStartX = null;
    this._touchStartY = null;

    if (distance > this.scrollThreshold) {
      // User scrolled — trigger default side
      this.activate(this.defaultSide);
    } else {
      // User tapped — determine side from touch X position
      var tapX = e.changedTouches[0].clientX;
      var splitX = window.innerWidth * (this.splitPercentage / 100);
      var side = tapX < splitX ? "left" : "right";
      this.activate(side);
    }
  }

  activate(side) {
    if (this.hasPlayed) return;
    this.hasPlayed = true;
    this.activeSide = side;

    const videoWrapper = side === "left" ? this.leftVideoWrapper : this.rightVideoWrapper;
    const video = this.getActiveVideo(side);

    if (video) {
      if (videoWrapper) {
        videoWrapper.classList.add("split-hero__video-wrapper--active");
      }
      if (this.staticMedia) {
        this.staticMedia.classList.add("is-hidden");
      }

      video.playbackRate = this.playbackSpeed;

      // Text fade — separate from content reveal
      const textEl = this.section.querySelector(".split-hero__text");
      const textFadeStart = textEl ? parseFloat(textEl.dataset.textFadeStart) : 0.3;
      const textAnimation = textEl ? textEl.dataset.textAnimation : "translate_up";
      let textFaded = false;

      const onTimeUpdate = () => {
        const progress = video.currentTime / video.duration;

        // Text fade at its own percentage
        if (!textFaded && textEl && progress >= textFadeStart) {
          textFaded = true;
          if (textAnimation === "translate_up") {
            textEl.classList.add("split-hero__text--fading-up");
          } else if (textAnimation === "translate_down") {
            textEl.classList.add("split-hero__text--fading-down");
          } else {
            textEl.classList.add("split-hero__text--fading-none");
          }
        }

        // Content crossfade
        if (progress >= this.contentRevealPoint) {
          video.removeEventListener("timeupdate", onTimeUpdate);
          this.startCrossfade();
        }
      };
      video.addEventListener("timeupdate", onTimeUpdate);

      // Cleanup when video ends
      video.addEventListener("ended", () => this.finish(), {once: true});

      video.play().catch(() => {
        // Autoplay blocked — fall back to immediate crossfade
        this.startCrossfade();
      });
    } else {
      // No video available — crossfade immediately
      this.startCrossfade();
    }

    // Update URL without reload
    this.updateUrl(side);

    // Disable click zones
    if (this.leftZone) this.leftZone.style.pointerEvents = "none";
    if (this.rightZone) this.rightZone.style.pointerEvents = "none";

    // Remove scroll listener
    this.unbindScrollTrigger();
  }

  /**
   * Start the crossfade: hero fades out, header + content fades in.
   */
  startCrossfade() {
    if (!this.overlayEl) return;

    // Fade out the overlay
    this.overlayEl.style.transition = "opacity 0.8s ease";
    this.overlayEl.style.opacity = "0";

    // Unlock body scroll and reveal content
    document.body.classList.remove("split-hero-active");

    // Show the correct section group based on clicked side
    this.showSectionGroup(this.activeSide || this.defaultSide);

    // After transition, fully hide the overlay
    const onTransitionEnd = () => {
      this.overlayEl.removeEventListener("transitionend", onTransitionEnd);
      this.overlayEl.classList.add("split-hero__overlay--hidden");
    };
    this.overlayEl.addEventListener("transitionend", onTransitionEnd);
  }

  /**
   * Final cleanup after video ends.
   */
  finish() {
    if (this.overlayEl && !this.overlayEl.classList.contains("split-hero__overlay--hidden")) {
      this.overlayEl.classList.add("split-hero__overlay--hidden");
    }
  }

  /**
   * Show the correct homepage sections based on the chosen side.
   * Sections are prefixed left_/right_ in templates/index.json.
   * @param {'left' | 'right'} side
   */
  showSectionGroup(side) {
    const leftSections = document.querySelectorAll('[id*="__left_"]');
    const rightSections = document.querySelectorAll('[id*="__right_"]');

    if (side === "left") {
      leftSections.forEach((el) => (el.style.display = "block"));
      rightSections.forEach((el) => (el.style.display = "none"));
    } else {
      leftSections.forEach((el) => (el.style.display = "none"));
      rightSections.forEach((el) => (el.style.display = "block"));
    }
  }

  /**
   * Get the active video element for the given side and current viewport.
   * @param {'left' | 'right'} side
   * @returns {HTMLVideoElement | null}
   */
  getActiveVideo(side) {
    const wrapper = side === "left" ? this.leftVideoWrapper : this.rightVideoWrapper;
    if (!wrapper) return null;

    const isDesktop = window.matchMedia("(min-width: 750px)").matches;
    const desktopVideo = wrapper.querySelector(".split-hero__video-elem--desktop video");
    const mobileVideo = wrapper.querySelector(".split-hero__video-elem--mobile video");
    // Fallback when only one variant is configured (no --desktop/--mobile class)
    const anyVideo = wrapper.querySelector(".split-hero__video-elem video");

    if (isDesktop && desktopVideo) return desktopVideo;
    if (!isDesktop && mobileVideo) return mobileVideo;
    return desktopVideo || mobileVideo || anyVideo;
  }

  /**
   * Update the URL with the chosen side without reloading the page.
   * @param {'left' | 'right'} side
   */
  updateUrl(side) {
    try {
      const url = new URL(window.location);
      url.searchParams.set("side", side);
      window.history.replaceState({}, "", url);
    } catch (e) {
      // Silently fail — URL update is non-critical
    }
  }

  /**
   * Skip the hero entirely — used for reduced motion preference.
   */
  skipHero() {
    if (this.overlayEl) {
      this.overlayEl.classList.add("split-hero__overlay--hidden");
    }
    document.body.classList.remove("split-hero-active");
    this.showSectionGroup(this.defaultSide);
  }

  /**
   * Remove the first-scroll listener and touch gesture listeners.
   */
  unbindScrollTrigger() {
    if (this._onFirstScroll) {
      window.removeEventListener("wheel", this._onFirstScroll);
    }
    if (this._boundTouchStart) {
      window.removeEventListener("touchstart", this._boundTouchStart);
      this._boundTouchStart = null;
    }
    if (this._boundTouchEnd) {
      window.removeEventListener("touchend", this._boundTouchEnd);
      this._boundTouchEnd = null;
    }
  }

  /**
   * Clean up event listeners and DOM state.
   */
  destroy() {
    this.unbindScrollTrigger();
    document.body.classList.remove("split-hero-active");
    if (this.overlayEl) {
      this.overlayEl.classList.add("split-hero__overlay--hidden");
    }
  }

  /**
   * Re-initialise (used by Theme Editor section reload).
   * @param {HTMLElement} parentEl
   */
  refresh(parentEl) {
    this.destroy();
    const newEl = parentEl.querySelector(".split-hero");
    if (newEl) {
      const newInstance = new SplitHero(newEl);
      newInstance.init();
      return newInstance;
    }
    return null;
  }
}

/* --------------------------------------------------------------------------
   Global initialisation
   -------------------------------------------------------------------------- */

const splitHeroInstances = new Map();

function initSplitHeroSections() {
  document.querySelectorAll(".split-hero").forEach((el) => {
    if (!splitHeroInstances.has(el)) {
      const hero = new SplitHero(el);
      hero.init();
      splitHeroInstances.set(el, hero);
    }
  });
}

document.addEventListener("DOMContentLoaded", initSplitHeroSections);

/* Theme Editor lifecycle */
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    const parentEl = document.getElementById("shopify-section-" + event.detail.sectionId);
    if (parentEl && parentEl.querySelector(".split-hero")) {
      const existingEl = parentEl.querySelector(".split-hero");
      if (existingEl) {
        const hero = new SplitHero(existingEl);
        hero.init();
        splitHeroInstances.set(existingEl, hero);
      }
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const parentEl = document.getElementById("shopify-section-" + event.detail.sectionId);
    if (parentEl) {
      const heroEl = parentEl.querySelector(".split-hero");
      if (heroEl && splitHeroInstances.has(heroEl)) {
        splitHeroInstances.get(heroEl).destroy();
        splitHeroInstances.delete(heroEl);
      }
    }
  });
}
